import {ChangeDetectorRef, Component, inject, OnDestroy, OnInit, signal} from "@angular/core";
import {CmsApiService} from "../../../../core/cms-api.service";
import {ModelStore} from "../../../../core/model-store/model-store";
import {Subscription} from "rxjs";
import {ActivatedRoute} from "@angular/router";
import {FieldSection, ReportUserGenerated} from "../../../../core/model-store/models/report-user-generated";
import {DragulaService} from "ng2-dragula";
import {
  stampReportUuid,
  gridReportUuid,
  rapportlisteMedBildeUuid,
  ReportViewType
} from "../../../../core/model-store/models/report-view-type";
import { getDefaultViewOptionsCheckboxValues, generateReport } from '../../generate-report-util'
import {ReportUserTemplate} from "../../../../core/model-store/models/report-user-template";
import {MatSelectChange} from "@angular/material/select";
import {saveAs} from "file-saver";
import {OperationService} from "../../../../operations/operation.service";
import {OperationModel} from "../../../../core/model-store/models/operation";
import {PrimusRouterService} from "../../../../core/primus-router.service";
import {UrlQueryParam} from "../../../../core/model-store/url-query-param";

@Component({
    selector: 'app-edit-report',
    templateUrl: './edit-report.component.html',
    styleUrls: ['./edit-report.component.scss'],
    standalone: false
})
export class EditReportComponent implements OnInit, OnDestroy {
  // Injections
  private readonly cdRef: ChangeDetectorRef = inject(ChangeDetectorRef);
  private readonly cms: CmsApiService = inject(CmsApiService);
  private readonly dragulaService: DragulaService = inject(DragulaService);
  private readonly modelStore: ModelStore = inject(ModelStore);
  private readonly operationService: OperationService = inject(OperationService);
  private readonly primusrouter: PrimusRouterService = inject(PrimusRouterService);
  private readonly route: ActivatedRoute = inject(ActivatedRoute);

  reportId: string;
  generatedReport: ReportUserGenerated;
  reportUserTemplates: ReportUserTemplate[];
  selectedReportUserTemplateId: string;
  reportViewTypes: ReportViewType[];
  selectedReportViewType: ReportViewType;
  showCreateNewReportSettingsArea: boolean = false;
  newReportSettings: ReportUserTemplate = null;
  showDeleteReportSettingsArea: boolean = false;
  showViewOptions = true;
  showFieldSelections = signal(true);
  dragulaId: string = 'HANDLES';
  operation : OperationModel = null
  subs = new Subscription();
  reportGenerationInterval: any = null;
  numPhotographsInReport: number = 1;
  numColumnsInReport: number = 2;
  selectedPhotographSize: string = 'small';
  previousRouteName: any = null;

  downloadPdfStarted = signal(false);
  reportGenerationInProgress = signal(false);

  viewOptionsCheckboxValues = getDefaultViewOptionsCheckboxValues();
  private paramMapSubscription: Subscription;

  constructor() {
    this.dragulaService.createGroup(this.dragulaId, {
      invalid: (el) => el.classList.contains('report-collapsible-list-child-item')
    });

    this.subs.add(this.dragulaService.dropModel(this.dragulaId)
      .subscribe(({ name, el, target, source, sourceModel, targetModel, item }) => {
        const targetPosition = targetModel.findIndex((field: any) => field.name === item.name);
        const sourcePosition = this.generatedReport.fieldSections.findIndex((field: any) => field.name === item.name);

        //When item is dropped, move the source item to the target position in this.availableFieldsKeyList
        this.generatedReport.fieldSections.splice(targetPosition, 0, this.generatedReport.fieldSections.splice(sourcePosition, 1)[0]);

        this.triggerUIUpdate();
      }));
  }

  ngOnDestroy() {
    this.dragulaService.destroy(this.dragulaId);
    this.subs.unsubscribe();
    this.paramMapSubscription.unsubscribe();

    if (this.reportGenerationInterval) {
      clearInterval(this.reportGenerationInterval);
      this.reportGenerationInterval = null;
    }
  }

  ngOnInit() {
    this.asyncInit().then();
  }

  private async asyncInit() {
    console.log('-------> ', this.primusrouter.prevStateName);
    if (this.primusrouter.prevStateName) {
      this.previousRouteName = this.primusrouter.prevStateName;
    }

    this.operation = this.modelStore.findModel('operation', 'reports_v2');
    await this.operation.loading;

    this.reportUserTemplates = this.modelStore.findAllModels('report_user_template');
    // @ts-ignore
    await this.reportUserTemplates.loading;

    this.reportViewTypes = this.modelStore.findAllModels('report_view_type');
    // @ts-ignore
    await this.reportViewTypes.loading;

    this.paramMapSubscription = this.route.paramMap.subscribe(params => {
      this.reportId = params.get('report_id');
    });

    if (this.reportId)  {
      this.generatedReport = this.modelStore.findModel('report_user_generated', this.reportId, new UrlQueryParam('timezone_offset', '' + new Date().getTimezoneOffset()));
      await this.generatedReport.loading;

      if (this.generatedReport.reportStatus === 'generating' || this.generatedReport.reportStatus === 'generated') {
        this.pollForFinishedPdf(this.reportId);
      }
      if (this.generatedReport.reportViewTypeId) {
        this.selectedReportViewType = this.reportViewTypes.find(viewType => viewType.id === this.generatedReport.reportViewTypeId);
      }

      let storedReportNumColumns = null;
      if (this.generatedReport.reportViewOptions) {
        for (const option of Object.keys(this.generatedReport.reportViewOptions)) {
          if (option === 'numPhotosPerObject') {
            this.numPhotographsInReport = this.generatedReport.reportViewOptions[option];
          }

          if (option === 'photographSize') {
            this.selectedPhotographSize = this.generatedReport.reportViewOptions[option];
          }

          if (option === 'numColumnsInReport') {
            this.numColumnsInReport = this.generatedReport.reportViewOptions[option];
            storedReportNumColumns =  this.generatedReport.reportViewOptions[option];
          }

          let checkbox = this.viewOptionsCheckboxValues.find(viewOption => viewOption.key === option);
          if (checkbox) {
            checkbox.value = this.generatedReport.reportViewOptions[option];
          }
        }
      }

      if (this.generatedReport.reportViewTypeId === stampReportUuid && storedReportNumColumns === null) {
        this.numColumnsInReport = 5;
      }

      this.showFieldSelections.set(this.generatedReport.reportViewTypeId !== stampReportUuid);
    }

    await this.fetchFieldSections();
  }

  private async fetchFieldSections() {

    let objectTypes = this.generatedReport.getObjectTypes();
    let fieldSections: FieldSection[] = [];
    if (objectTypes.length > 0) {
      //For each object type fetch fieldSearchResult and join the array returned via the name property
      for (const objectType of objectTypes) {
        let fieldSearchResult = await this.cms.getModelSections({object_type: objectType, usage: 'reports'});
        for (const section of fieldSearchResult) {
          //If fieldSearchResult does not contain a section with the same name-property, push it to the fieldSections array
          if (!fieldSections.some(fieldSection => fieldSection['name'] === section['name'] && fieldSection['path'] === section['path'])) {
            // @ts-ignore
            fieldSections.push(section);
          }
        }
      }

      this.generatedReport.applyFieldSectionsFromBackend(fieldSections);
      this.triggerUIUpdate();
    }
  }
  async navigateAwayFromPreview() {
    if (this.previousRouteName && this.previousRouteName === 'home.primus.search.artifact' && this.generatedReport.artifactList[0]['artifact_id'] !== null) {
      let artifactId = this.generatedReport.artifactList[0]['artifact_id'];
      await this.primusrouter.navigateState(this.previousRouteName, {artifactId: artifactId});
    } else if (this.previousRouteName) {
      await this.primusrouter.navigateState(this.previousRouteName)
    } else if (this.generatedReport.reportStatus === 'generated') {
      await this.primusrouter.navigateState('home.primus.reports.generatedReports')
    } else {
      await this.primusrouter.navigateState('home.primus.reports.reportDrafts')
    }
  }

  isAllImagesSelected() {
    return this.viewOptionsCheckboxValues.find((option) => option.key === 'alle_bilder').value;
  }

  isViewOptionChecked(optionKey: string) {
    if (!optionKey) {
      return false;
    }

    return this.viewOptionsCheckboxValues.find((option) => option.key === optionKey).value;
  }

  decrementNumPhotographsInReport() {
    if (this.numPhotographsInReport > 0) {
      this.numPhotographsInReport--;
    }
  }

  incrementNumPhotographsInReport() {
    this.numPhotographsInReport++
  }

  decrementNumColumnsInReport(minColumns = 2) {
    if (this.numColumnsInReport > minColumns) {
      this.numColumnsInReport--;
    }
  }

  incrementNumColumnsInReport(maxColumns = 4) {
    if (this.numColumnsInReport < maxColumns) {
      this.numColumnsInReport++;
    }
  }

  doShowCreateNewReportSettingsArea() {
    this.newReportSettings = new ReportUserTemplate();
    this.showCreateNewReportSettingsArea = true;
  }

  async doPersistNewReportSettings() {
    if (this.newReportSettings?.name && this.generatedReport != null) {
      this.newReportSettings.reportViewTypeId = this.generatedReport.reportViewTypeId;
      this.newReportSettings.reportViewOptions = this.generatedReport.reportViewOptions;
      this.newReportSettings.fieldSections = this.generatedReport.fieldSections;

      let modelCreated = this.modelStore.createModel({modelSingularKey: 'report_user_template', record: this.newReportSettings});
      await modelCreated.loading;

      this.newReportSettings = null;
      this.showCreateNewReportSettingsArea = false;
      this.reportUserTemplates = this.modelStore.findAllModels('report_user_template');

      //select the newly created user template
      this.selectedReportUserTemplateId = modelCreated.id;
      this.triggerUIUpdate();
    }
  }

  async persistSelectedUserTemplate() {
    if (this.selectedReportUserTemplateId) {
      for (const template of this.reportUserTemplates) {
        if (template.id === this.selectedReportUserTemplateId) {
          template.reportViewTypeId = this.generatedReport.reportViewTypeId;
          template.reportViewOptions = this.generatedReport.reportViewOptions;
          template.fieldSections = this.generatedReport.fieldSections;
          this.modelStore.updateModel({modelSingularKey: 'report_user_template', record: template});
        }
      }
    }
  }

  doCancelPersistingNewReportSettings() {
    this.newReportSettings = null;
    this.showCreateNewReportSettingsArea = false;
  }

  doShowDeleteReportSettingsArea() {
    this.showDeleteReportSettingsArea = true;
  }

  async doDeleteReportSettings() {
    if (this.selectedReportUserTemplateId) {
      for (const template of this.reportUserTemplates) {
        if (template.id === this.selectedReportUserTemplateId) {
          let deleteStatus = this.modelStore.deleteModel('report_user_template', template.id);
          await deleteStatus.loading;
          this.selectedReportUserTemplateId = null;
          this.reportUserTemplates = this.modelStore.findAllModels('report_user_template');
        }
      }
    }
    this.showDeleteReportSettingsArea = false
  }

  onUserTemplateSelected(event: MatSelectChange) {
    for (const template of this.reportUserTemplates) {
      if (template.id === event.value) {
        //Set reportUserTemplate ID to the selected value in the selectbox
        this.selectedReportUserTemplateId = template.id;

        //Set the templates report View type to the selected report
        this.selectedReportViewType = this.reportViewTypes.find(viewType => viewType.id === template.reportViewTypeId);

        //reset all checkboxes
        for (const viewOption of this.viewOptionsCheckboxValues) {
          viewOption.value = false;
        }

        //Check the correct boxes
        for (const option of Object.keys(template.reportViewOptions)) {
          let checkbox = this.viewOptionsCheckboxValues.find(viewOption => viewOption.key === option);
          if (checkbox) {
            checkbox.value = template.reportViewOptions[option];
          }
        }

        let options = {}

        for (const option of this.viewOptionsCheckboxValues) {
          if (template.reportViewOptions[option.key]) {
            options[option.key] = template.reportViewOptions[option.key]
          }
        }

        //Update the report preview with the checked values
        this.generatedReport.reportViewOptions = options;

        this.reportViewTypeChanged();

        //Set the fieldSections to the selected template fieldSections
        this.generatedReport.fieldSections = template.fieldSections;

        //Trigger UI update
        this.triggerUIUpdate();
      }
    }
  }

  async persistUserReport() {
    if (!this.generatedReport.reportViewOptions) {
      this.generatedReport.reportViewOptions = {};
    }

    // Persist any value other than the default (1), including 0 (no pictures).
    if (this.numPhotographsInReport !== 1) {
      let viewOptions = this.generatedReport.reportViewOptions;
      viewOptions['numPhotosPerObject'] = this.numPhotographsInReport;
    } else {
      let viewOptions = this.generatedReport.reportViewOptions;
      delete viewOptions['numPhotosPerObject'];
    }

    if (this.selectedPhotographSize === 'small') {
      let viewOptions = this.generatedReport.reportViewOptions;
      delete viewOptions['photographSize'];
    } else {
      let viewOptions = this.generatedReport.reportViewOptions;
      viewOptions['photographSize'] = this.selectedPhotographSize;
    }

    if ((this.generatedReport.reportViewTypeId === gridReportUuid || this.generatedReport.reportViewTypeId === stampReportUuid) && this.numColumnsInReport) {
      let viewOptions = this.generatedReport.reportViewOptions;
      viewOptions['numColumnsInReport'] = this.numColumnsInReport;
    } else {
      let viewOptions = this.generatedReport.reportViewOptions;
      delete viewOptions['numColumnsInReport'];
    }

    let newReportTemplate = this.modelStore.updateModel({modelSingularKey: 'report_user_generated', record: this.generatedReport});
    this.generatedReport = null;
    await newReportTemplate.loading;
    this.generatedReport = this.modelStore.findModel('report_user_generated', newReportTemplate.id);
  }

  async requestReportPdf(reportId) {
    this.reportGenerationInProgress.set(true);
    await this.persistUserReport();

    let newReportTemplate = this.modelStore.updateModel({modelSingularKey: 'report_user_generated', record: this.generatedReport});
    await newReportTemplate.loading;
    this.generatedReport = this.modelStore.findModel('report_user_generated', newReportTemplate.id, new UrlQueryParam('timezone_offset', '' + new Date().getTimezoneOffset()));

    const localStorageItem = localStorage.getItem('primus.client.main');

    await generateReport(this.generatedReport, localStorageItem, this.operationService, this.cms);
    this.generatedReport.reportStatus = 'generating';
    this.pollForFinishedPdf(reportId);

    this.triggerUIUpdate();
  }

  private pollForFinishedPdf(reportId) {
    this.reportGenerationInterval = setInterval(async () => {
      let tempReport = this.modelStore.reloadModel('report_user_generated', reportId, new UrlQueryParam('timezone_offset', '' + new Date().getTimezoneOffset()));
      await tempReport.loading;
      if (tempReport.reportStatus === 'generated') {
        this.reportGenerationInProgress.set(false);
        this.generatedReport = tempReport;

        await this.fetchFieldSections();
        this.triggerUIUpdate();
        clearInterval(this.reportGenerationInterval);
      }
    }, 15000);
  }

  async downloadSinglePdf() {
    this.downloadPdfStarted.set(true);
    let downloadUrl = await this.cms.requestReportPdfFromDms(this.generatedReport.id);
    if (downloadUrl.url) {
      let pdfFile = await fetch(downloadUrl.url);
      let pdfFileBlob = await pdfFile.blob();
      saveAs(pdfFileBlob, this.generatedReport.id + '.pdf');
    }
    this.downloadPdfStarted .set(false);
  }

  getRefreshToken() {
    const name = 'refresh_token';
    const ca = document.cookie.split(';');
    for (const cookie of ca) {
      const c = cookie.trim();
      if (c.startsWith(name)) {
        return c.substring(name.length + 1, c.length);
      }
    }
    return '';
  }

  doShowViewOptions() {
    this.showViewOptions = true;
    this.triggerUIUpdate();
  }

  doHideViewOptions() {
    this.showViewOptions = false;
    this.triggerUIUpdate();
  }

  toggleFieldExpanded(field: any) {
    field.isExpanded = !field.isExpanded;
  }

  toggleTopLevelField(section: any) {
    for (const field of section.fields) {
      field.isSelected = section.isSelected;
    }

    this.triggerUIUpdate();
  }

  private triggerUIUpdate() {
    // @ts-ignore
    this.generatedReport = {...this.generatedReport};

    // Manually trigger change detection
    this.cdRef.detectChanges();
  }

  toggleSubField(field: any) {
    field.isSelected = true;

    //If all sub-fields are deselected, deselect the parent field
    if (field.fields.every((field: any) => !field.isSelected)) {
      field.isSelected = false;
    }

    this.triggerUIUpdate();
  }

  markAllTopLevelFields(checked: boolean) {
    for (const value of this.generatedReport.fieldSections) {
      // @ts-ignore
      value.isSelected = checked;
      // @ts-ignore
      for (const field of value.fields) {
        field.isSelected = checked;
      }
    }

    this.triggerUIUpdate();
  }

  reportViewTypeChanged() {
    let storedReportNumColumns = null;

    this.generatedReport.reportViewTypeId = this.selectedReportViewType.id;
    for (const option of this.viewOptionsCheckboxValues) {
      if (option.value && !this.selectedReportViewType.viewChoices.includes(option.key)) {
        option.value = false;
      }
    }

    let options = {}

    for (const option of this.viewOptionsCheckboxValues) {
      if (option.value) {
        options[option.key] = option.value
      }
    }

    if (this.generatedReport.reportViewOptions) {
      for (let option of Object.keys(this.generatedReport.reportViewOptions)) {
        if (option === 'numPhotosPerObject') {
          this.numPhotographsInReport = this.generatedReport.reportViewOptions[option];
        }

        if (option === 'photographSize') {
          this.selectedPhotographSize = this.generatedReport.reportViewOptions[option];
        }

        if (option === 'numColumnsInReport') {
          this.numColumnsInReport = this.generatedReport.reportViewOptions[option];
          storedReportNumColumns =  this.generatedReport.reportViewOptions[option];
        }
      }
    }

    if (this.generatedReport.reportViewTypeId === stampReportUuid && storedReportNumColumns === null) {
      this.numColumnsInReport = 5;
    } else if (this.generatedReport.reportViewTypeId === gridReportUuid && storedReportNumColumns === null) {
      this.numColumnsInReport = 2;
    }

    this.generatedReport.reportViewOptions = options;

    this.showFieldSelections.set(this.generatedReport.reportViewTypeId !== stampReportUuid);
    this.triggerUIUpdate();
  }

  toggleViewChoice(viewChoice: any) {
    viewChoice.value = !viewChoice.value
    //find all viewChoices that are selected in a comma separated string
    let options = {}
    for (const option of this.viewOptionsCheckboxValues) {
      if (option.value) {
        options[option.key] = option.value
      }
    }

    this.generatedReport.reportViewOptions = options;

    this.triggerUIUpdate();
  }

  createTree(objects: any[]) {
    // Sort the objects alphabetically based on the artifact_name property
    objects.sort((a, b) => a.artifact_name.localeCompare(b.artifact_name));

    // Create a map to store the hierarchy
    const hierarchy = new Map();

    for (const obj of objects) {
      // Split the name property to get the hierarchy levels
      const levels = obj.name.split(' / ');
      // Start at the top level of the hierarchy
      let currentLevel = hierarchy;

      for (const level of levels) {
        let i = 0;
        // If the current level doesn't exist yet, create it
        if (!currentLevel.has(level)) {
          if (i === levels.length - 1) {
            // If this is the last level, add the complete JSON structure
            currentLevel.set(level, { ...obj, children: new Map() });
          } else {
            // Otherwise, create a new object with a children property
            // and keep the original name
            currentLevel.set(level, { name: obj.name, isSelected: false, isExpanded: false, children: new Map() });
          }
        }

        // Move to the next level
        currentLevel = currentLevel.get(level).children;
        i++;
      }
    }

    //Iterate over keys and delete all keys that have no children
    for (const [key, value] of hierarchy) {
      if (value.children.size === 0) {
        hierarchy.delete(key);
      }
    }

    return hierarchy;
  }

  updateReportPreview() {
    this.cdRef.detectChanges();
  }

  protected readonly rapportlisteMedBildeUuid = rapportlisteMedBildeUuid;
  protected readonly bilderapportMedOversiktsfelterUuid = gridReportUuid;
  protected readonly bilderapportId = stampReportUuid;
}
